/** @format */

import {MouseEvent, useEffect, useState} from 'react';

import {Box, Grid, LinearProgress, Link, Typography, useMediaQuery} from '@mui/material';
import Alert from '@mui/material/Alert';
import {useTheme} from '@mui/material/styles';
import {Trans} from 'react-i18next';
import {useNavigate} from 'react-router-dom';

import PrimaryMediumButton from 'components/buttons/PrimaryMediumButton';
import TertiaryMediumButton from 'components/buttons/TertiaryMediumButton';
import ConnectRows from 'components/connect/ConnectRows';
import BoxPlate from 'components/plates/BoxPlate';
import {useAuthContext} from 'contexts/AuthContext';
import {useCompanyContext} from 'contexts/CompanyContext';
import {useMixpanelContext} from 'contexts/MixpanelContext';
import {connectedTokens} from 'helpers/connectHelpers';
import {constants} from 'helpers/constants';
import {IsRuVersion, useCallbackOnReturn, useSearchParams} from 'helpers/routerHelpers';

import {OauthProviderToken} from 'graphql/generated/types';

import {ALL_PLATFORMS, initialAdType, rejectRuPlatforms} from 'helpers/adTypeHelpers';

import {CompanyName} from './CompanyName';
import InviteGuest from './InviteGuest';
import {
  useGetCurrentUserConnectQuery,
  useSetWizardFinishedMutation,
  useStartMutation,
  useUpdateCompanyMutation,
} from '../graphql/__generated__';

const PROVIDER_COUNT = 4;

interface Props {
  showInviteGuest?: boolean;
}

export const Connect = (props: Props) => {
  const navigate = useNavigate();

  const searchParams = useSearchParams();
  const companyId = searchParams.get('companyId');

  const {authToken} = useAuthContext();
  const {mixpanel} = useMixpanelContext();
  const {switchAdType} = useCompanyContext();

  const [showInviteGuest, setShowInviteGuest] = useState(false);
  const [companyName, setCompanyName] = useState('');
  const [nameEdited, setNameEdited] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const {loading, error, data, refetch} = useGetCurrentUserConnectQuery({
    skip: !companyId,
    variables: {companyId: companyId!},
  });
  const [setWizardFinished, {loading: setWizardFinishedLoading}] = useSetWizardFinishedMutation();
  const [start, {loading: startLoading}] = useStartMutation();
  const [updateCompany, {loading: updateCompanyLoading}] = useUpdateCompanyMutation();

  useCallbackOnReturn(refetch);

  useEffect(() => {
    if (!data?.currentUser?.company) {
      return;
    }
    setCompanyName(data.currentUser.company.name);
  }, [data]);

  if (error) {
    return <Alert severity='error'>{error.message}</Alert>;
  }
  if (!companyId) {
    return (
      <Alert severity='error'>
        <Trans>Кампания не найдена</Trans>
      </Alert>
    );
  }
  if (loading || !data?.currentUser?.company?.tokens) {
    return <LinearProgress style={{flex: 1}} />;
  }

  const {isWizardFinished, company, roles} = data.currentUser;
  const name = company.name;
  const tokens = company.tokens;
  const connectedCount = connectedTokens(tokens).filter(v => v.accounts.length > 0).length;

  const platforms = () => {
    if (IsRuVersion()) {
      return ALL_PLATFORMS;
    }
    return rejectRuPlatforms(ALL_PLATFORMS);
  };

  const renderTopText = () => {
    return (
      <>
        {!isWizardFinished && (
          <Box
            sx={{
              mt: 4,
            }}
          >
            <Typography variant='body1'>
              <Trans>
                Вы всегда сможете добавить больше рекламных кабинетов или отозвать уже выданные
                доступы в разделе «Настройки» «Управление проектами» после завершения регистрации.
              </Trans>
            </Typography>
          </Box>
        )}
        {isWizardFinished && (
          <Box
            sx={{
              mt: 4,
            }}
          >
            <Typography variant='body1'>
              <Trans>
                Вы приступили к добавлению нового проекта «{{name}}». Для дальнейшего подключения
                выберите рекламную площадку для которой нужно сделать подключение, кликнув «+».
                <br />
                Далее следуйте инструкции.
              </Trans>
            </Typography>
          </Box>
        )}
        <Box
          sx={{
            display: 'flex',
            mt: 3,
          }}
        >
          <Typography variant='body1'>{constants.CHECK_MARK}</Typography>
          <Box
            sx={{
              ml: 2,
            }}
          >
            <Typography variant='body1'>
              <Trans>
                Наш робот умеет только читать. Он не сможет ничего менять в ваших кабинетах.
              </Trans>
            </Typography>
          </Box>
        </Box>
      </>
    );
  };

  const renderCompany = () => {
    const handleSave = async (name: string) => {
      setNameEdited(false);
      await updateCompany({
        variables: {
          id: companyId,
          name: name,
        },
      });

      setCompanyName(name);
      setNameEdited(true);
      mixpanel.track('company_name_inplace_edited');
    };

    return (
      <Grid container>
        <Grid item xs={12} sm={5}>
          <CompanyName
            companyName={companyName}
            authTokenType={authToken.type}
            edited={nameEdited}
            loading={updateCompanyLoading}
            onSave={handleSave}
          />
        </Grid>
      </Grid>
    );
  };

  const renderInviteGuest = () => {
    if (authToken.type === 'GUEST') return null;

    const handleOpen = () => {
      setShowInviteGuest(true);
      mixpanel.track('click_wizard_show_invite_guest');
      if (window.dataLayer) {
        window.dataLayer.push({event: 'app_click_dostup'});
      }
    };

    const handleClose = () => {
      setShowInviteGuest(false);
    };

    return (
      <>
        <Box
          sx={{
            mt: 12,
          }}
        >
          <Typography variant='h1'>
            <Trans>Если у вас нет доступов от кабинетов</Trans>
          </Typography>

          <Box
            sx={{
              mt: 4,
            }}
          >
            <Typography variant='body1'>
              <Trans>
                Отправьте специальную страницу своему маркетологу, он выдаст доступы, после этого вы
                сможете продолжить работу.
              </Trans>
            </Typography>
          </Box>

          <Box
            sx={{
              mt: 6,
            }}
          >
            <TertiaryMediumButton onClick={handleOpen}>
              <Trans>Запросить доступ у специалиста</Trans>
            </TertiaryMediumButton>
          </Box>
        </Box>
        {showInviteGuest && <InviteGuest onClose={handleClose} />}
      </>
    );
  };

  const renderInstructionLink = () => {
    if (authToken.type === 'GUEST') return null;

    const handleInstructionClick = (event: MouseEvent<HTMLAnchorElement>) => {
      mixpanel.track('click_wizard_connect_instruction_button');
      if (window.dataLayer) {
        const link = event.currentTarget.href.replace('https://', '');
        window.dataLayer.push({event: `app_link_${link}`});
      }
      return true;
    };

    return (
      <>
        <Box
          sx={{
            mt: 12,
          }}
        >
          <Typography variant='h1'>
            <Trans>Если у вас остались вопросы</Trans>
          </Typography>

          <Box
            sx={{
              mt: 4,
            }}
          >
            <Typography variant='body1'>
              <Link
                href='https://adsensor.ru/instructions'
                target='_blank'
                aria-label='instruction-link'
                onClick={handleInstructionClick}
              >
                <Trans>Инструкция</Trans>
              </Link>
            </Typography>
          </Box>
        </Box>
      </>
    );
  };

  const renderButton = () => {
    if (connectedCount === 0) return null;

    const handleClick = async () => {
      mixpanel.track('click_wizard_connect_finish_button');

      const adType = initialAdType(tokens);
      switchAdType(adType);

      if (!isWizardFinished) {
        await setWizardFinished();
      }

      if (companyId) {
        await start({
          variables: {
            companyId,
          },
        });
      }

      if (authToken.type === 'GUEST') {
        navigate('/guest/finish');
        return;
      }

      if (isWizardFinished) {
        navigate('/home/settings/connect');
        return;
      }

      navigate('/home/main/wait');
    };

    return (
      <Box
        sx={{
          mt: 8,
        }}
      >
        <PrimaryMediumButton
          disabled={setWizardFinishedLoading || startLoading}
          onClick={handleClick}
          fullWidth
        >
          <Trans>Продолжить</Trans>
        </PrimaryMediumButton>
      </Box>
    );
  };

  const renderBottomText = () => {
    if (!isMobile) return null;
    if (connectedCount === PROVIDER_COUNT) return null;

    return (
      <Box
        sx={{
          mt: 6,
        }}
      >
        <BoxPlate>
          <Typography variant='body1'>
            <Trans>
              Если в телефоне не сохранены доступы, добавьте кабинеты на ПК и продолжайте
              пользоваться мобилой.
            </Trans>
          </Typography>
        </BoxPlate>
      </Box>
    );
  };

  return (
    <>
      <Typography variant='h1'>
        <Trans>Доступы</Trans>
      </Typography>
      {renderTopText()}
      <Box
        sx={{
          mt: 10,
        }}
      >
        {renderCompany()}
      </Box>
      <Box
        sx={{
          mt: 6,
        }}
      >
        <ConnectRows
          canSelectSubaccounts
          companyId={company.id}
          tokens={tokens as ReadonlyArray<OauthProviderToken>}
          platforms={platforms()}
          showMetaExtremismCaption={IsRuVersion()}
          userRole={roles}
          canEditLimits={false}
          needPay={false}
        />
      </Box>
      {renderButton()}
      {props.showInviteGuest && renderInviteGuest()}
      {renderInstructionLink()}
      {renderBottomText()}
    </>
  );
};
